import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  GlobeAltIcon,
  PhotoIcon,
  TagIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi } from '../../api/marketplace';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface ScheduledPost {
  id: string;
  title: string;
  content: string;
  platforms: string[];
  scheduledAt: Date;
  status: 'scheduled' | 'published' | 'failed' | 'cancelled';
  listingId?: string;
  media?: string[];
  hashtags?: string[];
  autoResponse?: boolean;
}

interface AutoResponse {
  id: string;
  name: string;
  triggerKeywords: string[];
  response: string;
  platforms: string[];
  isActive: boolean;
  usageCount: number;
  lastUsed?: Date;
}

interface MarketingCampaign {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  frequency: 'daily' | 'weekly' | 'monthly';
  posts: ScheduledPost[];
  status: 'active' | 'paused' | 'completed';
  totalPosts: number;
  publishedPosts: number;
}

export default function AutomatedMarketing() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('scheduled');
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [autoResponses, setAutoResponses] = useState<AutoResponse[]>([]);
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAutoResponseModal, setShowAutoResponseModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);

  const facilityId = 'default'; // Use a default facility ID, backend will handle facility resolution

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      switch (activeTab) {
        case 'scheduled':
          const posts = await marketplaceApi.getScheduledPosts(facilityId);
          setScheduledPosts(posts);
          break;
        case 'auto-responses':
          const responses = await marketplaceApi.getAutoResponses(facilityId);
          setAutoResponses(responses);
          break;
        case 'campaigns':
          const campaignData = await marketplaceApi.getMarketingCampaigns(facilityId);
          setCampaigns(campaignData);
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScheduledPost = async (formData: any) => {
    try {
      await marketplaceApi.createScheduledPost(facilityId, formData);
      toast.success('Post scheduled successfully!');
      setShowCreateModal(false);
      loadData();
    } catch (error) {
      console.error('Error scheduling post:', error);
      toast.error('Failed to schedule post');
    }
  };

  const handleCreateAutoResponse = async (formData: any) => {
    try {
      await marketplaceApi.createAutoResponse(facilityId, formData);
      toast.success('Auto-response created successfully!');
      setShowAutoResponseModal(false);
      loadData();
    } catch (error) {
      console.error('Error creating auto-response:', error);
      toast.error('Failed to create auto-response');
    }
  };

  const handleCreateCampaign = async (formData: any) => {
    try {
      await marketplaceApi.createMarketingCampaign(facilityId, formData);
      toast.success('Campaign created successfully!');
      setShowCampaignModal(false);
      loadData();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    }
  };

  const handleDeleteItem = async (type: string, id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      switch (type) {
        case 'post':
          await marketplaceApi.deleteScheduledPost(facilityId, id);
          break;
        case 'response':
          await marketplaceApi.deleteAutoResponse(facilityId, id);
          break;
        case 'campaign':
          await marketplaceApi.deleteMarketingCampaign(facilityId, id);
          break;
      }
      toast.success('Item deleted successfully!');
      loadData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-cyan-100 text-cyan-800';
      case 'published':
        return 'bg-emerald-100 text-emerald-800';
      case 'failed':
        return 'bg-rose-100 text-rose-800';
      case 'cancelled':
        return 'bg-violet-100 text-violet-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <ClockIcon className="h-4 w-4" />;
      case 'published':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4" />;
      case 'cancelled':
        return <XCircleIcon className="h-4 w-4" />;
      default:
        return <ExclamationTriangleIcon className="h-4 w-4" />;
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Automated Marketing</h2>
          <p className="mt-1 text-sm text-slate-500">
            Schedule posts, create auto-responses, and manage marketing campaigns
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'scheduled'
                ? 'border-blue-500 text-cyan-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
            }`}
          >
            Scheduled Posts
          </button>
          <button
            onClick={() => setActiveTab('auto-responses')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'auto-responses'
                ? 'border-blue-500 text-cyan-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
            }`}
          >
            Auto-Responses
          </button>
          <button
            onClick={() => setActiveTab('campaigns')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'campaigns'
                ? 'border-blue-500 text-cyan-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
            }`}
          >
            Campaigns
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'scheduled' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Scheduled Posts</h3>
            <button
              onClick={() => {
                setEditingItem(null);
                setShowCreateModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Schedule Post
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scheduledPosts.map((post) => (
              <div key={post.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-slate-900">{post.title}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(post.status)}`}>
                      {getStatusIcon(post.status)}
                      <span className="ml-1 capitalize">{post.status}</span>
                    </span>
                  </div>

                  <p className="text-sm text-slate-600 mb-4 line-clamp-3">{post.content}</p>

                  <div className="flex items-center space-x-2 mb-4">
                    {(post.platforms || []).filter(Boolean).map((platform) => (
                      <span
                        key={platform}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800"
                      >
                        {platform}
                      </span>
                    ))}
                  </div>

                  <div className="text-sm text-slate-500 mb-4">
                    Scheduled for: {formatDate(post.scheduledAt)}
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingItem(post);
                        setShowCreateModal(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteItem('post', post.id)}
                      className="inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {scheduledPosts.length === 0 && (
            <div className="text-center py-12">
              <ClockIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No scheduled posts</h3>
              <p className="mt-1 text-sm text-slate-500">
                Get started by scheduling your first post.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'auto-responses' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Auto-Responses</h3>
            <button
              onClick={() => {
                setEditingItem(null);
                setShowAutoResponseModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Auto-Response
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {autoResponses.map((response) => (
              <div key={response.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-slate-900">{response.name}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      response.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-800'
                    }`}>
                      {response.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Trigger Keywords:</p>
                    <div className="flex flex-wrap gap-1">
                      {(response.triggerKeywords || []).filter(Boolean).map((keyword) => (
                        <span
                          key={keyword}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>

                  <p className="text-sm text-slate-600 mb-4 line-clamp-3">{response.response}</p>

                  <div className="flex items-center space-x-2 mb-4">
                    {(response.platforms || []).filter(Boolean).map((platform) => (
                      <span
                        key={platform}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800"
                      >
                        {platform}
                      </span>
                    ))}
                  </div>

                  <div className="text-sm text-slate-500 mb-4">
                    Used {response.usageCount || 0} times
                    {response.lastUsed && (
                      <span> • Last used: {formatDate(response.lastUsed)}</span>
                    )}
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingItem(response);
                        setShowAutoResponseModal(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteItem('response', response.id)}
                      className="inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {autoResponses.length === 0 && (
            <div className="text-center py-12">
              <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No auto-responses</h3>
              <p className="mt-1 text-sm text-slate-500">
                Create auto-responses to automatically reply to common inquiries.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'campaigns' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Marketing Campaigns</h3>
            <button
              onClick={() => {
                setEditingItem(null);
                setShowCampaignModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Campaign
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-slate-900">{campaign.name}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      campaign.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                      campaign.status === 'paused' ? 'bg-amber-100 text-amber-800' :
                      'bg-violet-100 text-violet-800'
                    }`}>
                      {campaign.status === 'active' ? <PlayIcon className="h-4 w-4" /> :
                       campaign.status === 'paused' ? <PauseIcon className="h-4 w-4" /> :
                       <CheckCircleIcon className="h-4 w-4" />}
                      <span className="ml-1 capitalize">{campaign.status}</span>
                    </span>
                  </div>

                  <p className="text-sm text-slate-600 mb-4">{campaign.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Start Date:</span>
                      <span className="text-slate-900">{formatDate(campaign.startDate)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">End Date:</span>
                      <span className="text-slate-900">{formatDate(campaign.endDate)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Frequency:</span>
                      <span className="text-slate-900 capitalize">{campaign.frequency}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Progress:</span>
                      <span className="text-slate-900">{campaign.publishedPosts}/{campaign.totalPosts}</span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingItem(campaign);
                        setShowCampaignModal(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteItem('campaign', campaign.id)}
                      className="inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {campaigns.length === 0 && (
            <div className="text-center py-12">
              <CalendarIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No campaigns</h3>
              <p className="mt-1 text-sm text-slate-500">
                Create marketing campaigns to automate your posting schedule.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Scheduled Post Modal */}
      <BrandModal open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidthClass="max-w-md">
        <BrandModalHeader
          title={editingItem ? 'Edit Scheduled Post' : 'Schedule New Post'}
          onClose={() => setShowCreateModal(false)}
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            handleCreateScheduledPost({
              title: formData.get('title'),
              content: formData.get('content'),
              platforms: formData.getAll('platforms'),
              scheduledAt: formData.get('scheduledAt'),
            });
          }}
        >
          <BrandModalBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    defaultValue={editingItem?.title || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter post title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Content *
                  </label>
                  <textarea
                    name="content"
                    defaultValue={editingItem?.content || ''}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter post content..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Platforms *
                  </label>
                  <div className="space-y-2">
                    {['whatsapp', 'facebook', 'instagram', 'telegram', 'birdMarketplace'].map((platform) => (
                      <label key={platform} className="flex items-center">
                        <input
                          type="checkbox"
                          name="platforms"
                          value={platform}
                          defaultChecked={editingItem?.platforms?.includes(platform)}
                          className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-slate-700 capitalize">{platform}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Schedule Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    name="scheduledAt"
                    defaultValue={editingItem?.scheduledAt ? new Date(editingItem.scheduledAt).toISOString().slice(0, 16) : ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
          </BrandModalBody>
          <BrandModalFooter>
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary text-sm">
              {editingItem ? 'Update Post' : 'Schedule Post'}
            </button>
          </BrandModalFooter>
        </form>
      </BrandModal>

      {/* Create/Edit Auto-Response Modal */}
      <BrandModal open={showAutoResponseModal} onClose={() => setShowAutoResponseModal(false)} maxWidthClass="max-w-md">
        <BrandModalHeader
          title={editingItem ? 'Edit Auto-Response' : 'Create Auto-Response'}
          onClose={() => setShowAutoResponseModal(false)}
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            handleCreateAutoResponse({
              name: formData.get('name'),
              triggerKeywords: formData.get('triggerKeywords')?.toString().split(',').map(k => k.trim()) || [],
              response: formData.get('response'),
              platforms: formData.getAll('platforms'),
              isActive: formData.get('isActive') === 'true',
            });
          }}
        >
          <BrandModalBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingItem?.name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter auto-response name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Trigger Keywords (comma-separated) *
                  </label>
                  <input
                    type="text"
                    name="triggerKeywords"
                    defaultValue={editingItem?.triggerKeywords?.join(', ') || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="price, cost, available, etc."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Response *
                  </label>
                  <textarea
                    name="response"
                    defaultValue={editingItem?.response || ''}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter auto-response message..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Platforms *
                  </label>
                  <div className="space-y-2">
                    {['whatsapp', 'facebook', 'instagram', 'telegram'].map((platform) => (
                      <label key={platform} className="flex items-center">
                        <input
                          type="checkbox"
                          name="platforms"
                          value={platform}
                          defaultChecked={editingItem?.platforms?.includes(platform)}
                          className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-slate-700 capitalize">{platform}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      value="true"
                      defaultChecked={editingItem?.isActive !== false}
                      className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-slate-700">Active</span>
                  </label>
                </div>
              </div>
          </BrandModalBody>
          <BrandModalFooter>
            <button
              type="button"
              onClick={() => setShowAutoResponseModal(false)}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary text-sm">
              {editingItem ? 'Update Auto-Response' : 'Create Auto-Response'}
            </button>
          </BrandModalFooter>
        </form>
      </BrandModal>

      {/* Create/Edit Campaign Modal */}
      <BrandModal open={showCampaignModal} onClose={() => setShowCampaignModal(false)} maxWidthClass="max-w-md">
        <BrandModalHeader
          title={editingItem ? 'Edit Campaign' : 'Create Marketing Campaign'}
          onClose={() => setShowCampaignModal(false)}
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            handleCreateCampaign({
              name: formData.get('name'),
              description: formData.get('description'),
              startDate: formData.get('startDate'),
              endDate: formData.get('endDate'),
              frequency: formData.get('frequency'),
            });
          }}
        >
          <BrandModalBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingItem?.name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter campaign name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    defaultValue={editingItem?.description || ''}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter campaign description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    defaultValue={editingItem?.startDate ? new Date(editingItem.startDate).toISOString().split('T')[0] : ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    defaultValue={editingItem?.endDate ? new Date(editingItem.endDate).toISOString().split('T')[0] : ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Frequency *
                  </label>
                  <select
                    name="frequency"
                    defaultValue={editingItem?.frequency || 'daily'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>
          </BrandModalBody>
          <BrandModalFooter>
            <button
              type="button"
              onClick={() => setShowCampaignModal(false)}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary text-sm">
              {editingItem ? 'Update Campaign' : 'Create Campaign'}
            </button>
          </BrandModalFooter>
        </form>
      </BrandModal>
    </div>
  );
}
